import { useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase/client";
import { usePortalLanguage, tLang } from "@/contexts/PortalLanguageContext";

const NAVY  = "#1A1A2E";
const EMBER = "#FF6B35";

export default function News() {
  const { t } = usePortalLanguage();
  const { pathname } = useLocation();
  const locale = pathname.startsWith("/fr") ? "fr" : pathname.startsWith("/es") ? "es" : "en";

  const [email,  setEmail]  = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error,  setError]  = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t("app.form.invalidEmail", "Please enter a valid email address."));
      return;
    }

    setStatus("sending");

    const { error: dbError } = await supabase
      .from("early_access_signups")
      .insert({ email: email.trim(), source: "news", locale, created_at: new Date().toISOString() });

    if (dbError && dbError.code !== "23505") {
      setStatus("error");
      setError(t("app.form.error", "Something went wrong. Please try again."));
      return;
    }

    setStatus("sent");
  };

  return (
    <>
      <Helmet>
        <title>{tLang(locale, "news.meta.title", "News — Fyrris")}</title>
        <meta name="description" content={tLang(locale, "news.meta.description")} />
        {/* No article source exists yet — this page is an early-access signup
            form with a "TODO: Replace with real articles" placeholder below.
            Indexing it would put a thin page under a title that promises
            updates and industry insight.
            TO UNDO once real articles ship: delete this meta line, and re-add
            the `news` entry to PAGES in scripts/route-map.mjs so the page
            returns to the sitemap and the prerender set. */}
        <meta name="robots" content="noindex" />
        <meta property="og:title" content={tLang(locale, "news.meta.ogTitle")} />
        <meta property="og:description" content={tLang(locale, "news.meta.ogDescription")} />
        <meta property="og:type" content="website" />
      </Helmet>

      <section
        className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-20"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-xl mx-auto text-center">
          <p
            className="inline-block text-xs font-semibold tracking-widest uppercase mb-6 px-3 py-1 rounded-full"
            style={{ backgroundColor: `${EMBER}22`, color: EMBER }}
          >
            {t("news.eyebrow", "Coming Soon")}
          </p>

          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-5">
            {t("news.headline", "Stories from the HPB industry.")}
          </h1>

          <p className="text-lg text-gray-400 mb-12 leading-relaxed">
            {t(
              "news.subhead",
              "Insights, product news, and retailer stories. Coming soon."
            )}
          </p>

          {status === "sent" ? (
            <p className="text-sm font-medium" style={{ color: EMBER }}>
              {t("news.successMessage", "You're on the list — we'll let you know when we publish.")}
            </p>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3 justify-center"
              noValidate
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("news.emailPlaceholder", "your@email.com")}
                className="flex-1 sm:max-w-xs rounded-xl border border-gray-700 bg-gray-900 text-white placeholder-gray-500 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                disabled={status === "sending"}
              />
              <button
                type="submit"
                disabled={status === "sending"}
                className="inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-60 whitespace-nowrap"
                style={{ backgroundColor: EMBER }}
              >
                {status === "sending"
                  ? t("news.submitting", "Submitting…")
                  : t("news.cta", "Get notified")}
              </button>
            </form>
          )}

          {error && (
            <p className="mt-3 text-xs text-red-400">{error}</p>
          )}
        </div>
      </section>

      {/* ── Placeholder article cards ──────────────────────────────────── */}
      {/* TODO: Replace with real articles from CMS/database when content is ready */}
      <section className="bg-gray-950 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-600 mb-8 text-center">
            {t("news.preview.label", "Upcoming stories")}
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Card 1 — Industry News */}
            <article className="rounded-2xl border border-gray-800 bg-gray-900/60 p-6 flex flex-col gap-4 relative overflow-hidden">
              <div className="flex items-center justify-between">
                <span
                  className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: `${EMBER}22`, color: EMBER }}
                >
                  {t("news.categories.industry", "Industry News")}
                </span>
                <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-800 text-gray-500">
                  {t("news.badges.comingSoon", "Coming Soon")}
                </span>
              </div>
              <div className="space-y-2 flex-1">
                <p className="text-xs text-gray-600">
                  {t("news.preview.datePlaceholder", "Spring 2026")}
                </p>
                <h3 className="text-base font-semibold text-white leading-snug">
                  {t("news.preview.card1.title", "AR is reshaping how HPB shoppers decide")}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {t("news.preview.card1.desc", "How augmented reality is changing the pre-purchase journey for fireplace and patio buyers across North America.")}
                </p>
              </div>
              {/* Decorative blur spot */}
              <div
                className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full blur-2xl opacity-20 pointer-events-none"
                style={{ backgroundColor: EMBER }}
              />
            </article>

            {/* Card 2 — Product Updates */}
            <article className="rounded-2xl border border-gray-800 bg-gray-900/60 p-6 flex flex-col gap-4 relative overflow-hidden">
              <div className="flex items-center justify-between">
                <span
                  className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: "#00BFA522", color: "#00BFA5" }}
                >
                  {t("news.categories.product", "Product Updates")}
                </span>
                <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-800 text-gray-500">
                  {t("news.badges.comingSoon", "Coming Soon")}
                </span>
              </div>
              <div className="space-y-2 flex-1">
                <p className="text-xs text-gray-600">
                  {t("news.preview.datePlaceholder", "Spring 2026")}
                </p>
                <h3 className="text-base font-semibold text-white leading-snug">
                  {t("news.preview.card2.title", "Fyrris Portal v2: what's new for retailers")}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {t("news.preview.card2.desc", "A walkthrough of the latest retailer portal improvements — from faster onboarding to richer product analytics.")}
                </p>
              </div>
              <div
                className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full blur-2xl opacity-20 pointer-events-none"
                style={{ backgroundColor: "#00BFA5" }}
              />
            </article>

            {/* Card 3 — Retailer Stories */}
            <article className="rounded-2xl border border-gray-800 bg-gray-900/60 p-6 flex flex-col gap-4 relative overflow-hidden">
              <div className="flex items-center justify-between">
                <span
                  className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full bg-purple-900/30 text-purple-400"
                >
                  {t("news.categories.stories", "Retailer Stories")}
                </span>
                <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-800 text-gray-500">
                  {t("news.badges.comingSoon", "Coming Soon")}
                </span>
              </div>
              <div className="space-y-2 flex-1">
                <p className="text-xs text-gray-600">
                  {t("news.preview.datePlaceholder", "Spring 2026")}
                </p>
                <h3 className="text-base font-semibold text-white leading-snug">
                  {t("news.preview.card3.title", "How one HPB store doubled its closing rate")}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {t("news.preview.card3.desc", "A founding retailer shares how offering in-store AR demos changed the conversation with every walk-in customer.")}
                </p>
              </div>
              <div
                className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full blur-2xl opacity-20 pointer-events-none"
                style={{ backgroundColor: "#7C3AED" }}
              />
            </article>
          </div>
        </div>
      </section>
    </>
  );
}
